import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useI18n();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("¡Bienvenido!");
            navigate("/");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Credenciales incorrectas");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="login-page">
            <div className="w-full max-w-md relative">
                <div className="glow-orb bg-p2w-green w-[300px] h-[300px] -top-20 -right-20 opacity-20" />
                <div className="relative rounded-2xl border border-p2w-line bg-p2w-surface p-8 shadow-glow-violet">
                    <h1 className="font-sora font-extrabold text-3xl tracking-tight">{t("auth.login_title")}</h1>
                    <p className="mt-1 text-p2w-gray text-sm">{t("auth.login_sub")}</p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-p2w-gray text-xs uppercase tracking-widest">
                                {t("auth.email")}
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                data-testid="login-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-p2w-gray text-xs uppercase tracking-widest">
                                {t("auth.password")}
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                data-testid="login-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="login-submit"
                            disabled={submitting}
                            className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold py-6 rounded-xl"
                        >
                            {submitting ? "Entrando…" : t("auth.login_cta")}
                        </Button>
                    </form>

                    <div className="mt-4 text-center">
                        <Link
                            to="/forgot-password"
                            data-testid="login-forgot-link"
                            className="text-sm text-p2w-gray hover:text-p2w-green hover:underline transition-colors"
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>
                    </div>

                    <p className="mt-6 text-sm text-p2w-gray text-center">
                        {t("auth.no_account")}{" "}
                        <Link to="/registro" className="text-p2w-green hover:underline font-semibold">
                            {t("nav.register")}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
